//@flow
import React, {useCallback, useEffect, useRef, useState} from 'react';
import CustomModal from '../CustomModal/CustomModal';
import {makeStyles} from '@material-ui/core/styles';

import Cropper from 'react-easy-crop';
//$FlowFixMe
import type {Point, Area} from 'react-easy-crop/types';

import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  Hidden,
} from '@material-ui/core';
import {colors} from '../../assets/styles/colors';
import {ArrowForwardIos, InfoOutlined, OpenWith} from '@material-ui/icons';
import CustomButton from '../CustomButton/CustomButton';

import type {CompressImageAdvancedParams} from '../../utils/image';
import ZoomSlider from './ZoomSlider';
import AspectSquareIcon from '../icons/AspectSquareIcon';
import AspectRectangleIcon from '../icons/AspectRectangleIcon';
import getCroppedImg from './cropImage';
import ColorInput from '../ColorInput';
import {useStoreColorSettings} from '../../modules/ui/utils';

import type {CropOptions} from './cropImage';

export type ImageToCrop = {
  file: File,
  preview: string,
  aspect?: number,
};

export type DynamicAspectRatio = {
  rectangle: {
    aspect: number,
    label?: string,
  },
  square: {
    aspect: number,
    label?: string,
  },
};

type Props = {
  open: boolean,
  onClose: () => void,
  aspect?: number,
  images: ImageToCrop[],
  onSubmit: (result: ImageToCrop[]) => void,
  keepFormat?: boolean,
  dynamicAspectRatio?: DynamicAspectRatio,
  restrictPosition?: boolean,
  compressOptions?: CompressImageAdvancedParams & {
    threshold?: number,
  },
};

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    backgroundColor: 'rgba(244,244,246,.8)',
    zIndex: 10,
    top: 0,
    left: 0,
    cursor: 'progress',
  },
  loaderMessage: {
    fontSize: '1rem',
    color: colors.grey900,
    fontWeight: 600,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'center',
  },
  title: {
    fontSize: '1.375rem',
    fontWeight: 600,
    textAlign: 'center',
  },
  tip: {
    fontSize: '0.875rem',
    margin: '0 0 0.5rem 0',
    textAlign: 'center',
    color: colors.grey700,

    '&[data-error="true"]': {
      color: colors.error,
      fontWeight: 600,
    },
  },
  cropContainer: {
    position: 'relative',
    backgroundColor: colors.grey200,
    borderRadius: '.75rem',
    height: '45vh',
    maxHeight: 500,
    minHeight: 300,
    display: 'flex',
    justifyContent: 'center',

    [theme.breakpoints.up('sm')]: {
      height: '50vh',
    },
    [theme.breakpoints.up('md')]: {
      height: '60vh',
    },
  },
  cropArea: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '100%',
    padding: '.1rem',
    height: 'auto',
    width: '100%',
    maxHeight: '100%',

    '& > div > div': {
      height: '100%',
    },
    '& img': {
      height: '100%',
      width: 'auto',
      objectFit: 'contain',
    },
    '& .ReactCrop': {
      height: '100%',
    },
    '& .ReactCrop__crop-selection': {
      border: `1px solid ${colors.grey500}`,
    },
    '& .ReactCrop__drag-handle::after': {
      width: '36px',
      height: '36px',
      border: 'none',
      backgroundColor: 'transparent',
    },
    '& .ReactCrop .ord-nw::after': {
      top: '2px',
      left: '2px',
      borderTop: '5px solid white',
      borderLeft: '5px solid white',
    },
    '& .ReactCrop .ord-ne::after': {
      top: '2px',
      right: '2px',
      borderTop: '5px solid white',
      borderRight: '5px solid white',
    },
    '& .ReactCrop .ord-sw::after': {
      bottom: '2px',
      left: '2px',
      borderBottom: '5px solid white',
      borderLeft: '5px solid white',
    },
    '& .ReactCrop .ord-se::after': {
      bottom: '2px',
      right: '2px',
      borderBottom: '5px solid white',
      borderRight: '5px solid white',
    },
  },
  hintContainer: {
    margin: theme.spacing(3),
    pointerEvents: 'none',
    display: 'flex',
    position: 'absolute',
    // left: '50%',
    // transform: 'translate(-50%, 0)',
    alignItems: 'center',
    bottom: 0,
    backgroundColor: 'rgba(58, 58, 73, 0.7)',
    color: 'white',
    borderRadius: 8,
    padding: '3px 8px',
    '& p': {
      textAlign: 'center',
      fontSize: 12,
      margin: 0,
      marginLeft: 8,
    },
  },
  crop_image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: '1rem',
    padding: '1rem 0',
    borderTop: `1px solid ${colors.grey300}`,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: '.75rem',
  },
  arrowButton: {
    background: colors.grey200,
    color: colors.grey800,
  },
  navigationButton: {
    background: 'white',
    borderRadius: '50%',
    border: `1px solid ${colors.grey300}`,
    color: colors.grey800,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,

    '&:hover': {
      background: 'rgba(255,255,255,.8)',
    },

    '&[data-position="left"]': {
      left: '.5rem',
    },

    '&[data-position="right"]': {
      right: '.5rem',
    },
  },
  submitButton: {
    maxWidth: '200px',
    fontSize: '1rem',
    fontWeight: 600,
    paddingBlock: '.6rem',
  },
  tooltip: {
    marginLeft: theme.spacing(2),
    backgroundColor: '#F4F4F6',
    borderRadius: '8px',
    textAlign: 'center',
    maxWidth: 240,
  },
  textTooltip: {
    textAlign: 'left!important',
    color: ' #717190',
    fontSize: '10px',
    '& span': {
      marginLeft: theme.spacing(1),
    },
  },
  infoBtn: {
    color: '#9595AB',
  },
  aspectButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    padding: '.5rem 0',
  },
  aspectButton: {
    borderRadius: '.5rem',
  },
  aspectButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  sliderContainer: {
    width: '100%',
    margin: '.75rem auto',
  },
  customBgSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    padding: '.5rem 0',
    justifyContent: 'center',
    width: '100%',
  },
  colorInput: {
    width: '100%',
    maxWidth: 180,
    '&[data-disabled="true"]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  colorInputLabel: {
    display: 'none',

    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  modalPaper: {
    padding: '1rem',
    maxHeight: 'initial',
    margin: 0,
    height: 'auto',
    overflowX: 'hidden',
    width: '100%',
    borderRadius: '1rem',
    maxWidth: 800,
    boxShadow: '0px -4px 20px 0px rgba(0, 0, 0, 0.06)',
  },
}));

const ZOOM_CONFIG = {
  min: 0.3,
  max: 3,
};

type CropRef = {
  position: Point,
  cropResult?: Area,
  aspect: number,
  zoom: number,
};

export default function CropModal(props: Props) {
  const {
    open,
    onClose,
    aspect = 1,
    onSubmit,
    images,
    keepFormat,
    dynamicAspectRatio,
    compressOptions,
    restrictPosition = false,
  } = props;

  const classes = useStyles();
  const colorConfig = useStoreColorSettings();

  const [cropImageProgress, setCropImageProgress] = useState(0);

  const [cropAspectRatio, setCropAspectRatio] = useState(() => {
    if (!dynamicAspectRatio) return aspect;
    if (dynamicAspectRatio.square.aspect === aspect) return aspect;
    if (dynamicAspectRatio.rectangle.aspect === aspect) return aspect;
    return dynamicAspectRatio.square.aspect;
  });

  const cropAreaRef = useRef(null);

  const cropListRef = useRef<CropRef[]>(
    images.map(() => ({
      aspect,
      height: 100,
      position: {x: 0, y: 0},
      zoom: 1,
    }))
  );

  useEffect(() => {
    cropListRef.current = images.map(crop => ({
      zoom: 1,
      aspect: crop.aspect || cropAspectRatio,
      position: {x: 0, y: 0},
    }));
  }, [cropAspectRatio, images]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [crop, setCrop] = useState<Point>({x: 0, y: 0});
  const [backgroundColor, setBackgroundColor] = useState(
    colorConfig ? colorConfig.primaryBackgroundColor : '#F5F0FF'
  );
  const [enabledCustomBgColor, setEnabledCustomBgColor] = useState(false);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!cropListRef.current) return;
    setCrop(null);

    if (cropAreaRef.current) {
      cropAreaRef.current.animate([{opacity: 1}, {opacity: 0}], {
        duration: 200,
        easing: 'ease-in-out',
      });
    }
    const timeout = setTimeout(() => {
      const currentCrop = cropListRef.current[currentIndex];
      setCrop(currentCrop.position);
      if (cropAreaRef.current) {
        cropAreaRef.current.animate([{opacity: 0}, {opacity: 1}], {
          duration: 300,
          easing: 'ease-in-out',
        });
      }

      setZoom(currentCrop.zoom);
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, [currentIndex, cropAspectRatio]);

  const handleCropImages = async () => {
    const croppedImages: ImageToCrop[] = [];

    const cropList = cropListRef.current.map((crop, index) => {
      return {
        image: images[index].preview,
        file: images[index].file,
        crop: crop.cropResult,
      };
    });

    setCropImageProgress(0);

    for (const cropItem of cropList) {
      const cropOptions: CropOptions = {
        format: keepFormat ? cropItem.file.type : 'image/jpeg',
        customBackground: enabledCustomBgColor ? backgroundColor : undefined,
      };

      if (compressOptions) {
        cropOptions.maxHeight = compressOptions.maxHeight;
        cropOptions.maxWidth = compressOptions.maxWidth;
        cropOptions.quality = compressOptions.quality;
      }

      const result = await getCroppedImg(
        cropItem.image,
        cropItem.crop,
        cropOptions
      );

      if (!result) continue;
      const [file, preview] = result;

      croppedImages.push({file, preview});

      setCropImageProgress(prev => prev + 1);
    }

    return croppedImages;
  };

  const validateAllCropped = () => {
    const isAllCropped = cropListRef.current.every(crop => !!crop.cropResult);
    return isAllCropped;
  };

  const handleConfirm = async () => {
    setError(null);
    const isAllCropped = validateAllCropped();

    if (!isAllCropped) {
      setError('⚠ Debes recortar todas las imágenes');

      setTimeout(() => {
        setError(null);
      }, 5000);
      return;
    }

    setLoading(true);
    const croppedImages = await handleCropImages()
      .catch(error => {
        setError('Error al recortar las imágenes');

        setTimeout(() => {
          setError(null);
        }, 4000);

        return [];
      })
      .finally(() => {
        setLoading(false);
      });

    onSubmit(croppedImages);
  };

  const currentImage = images[currentIndex];

  const hasNext = !!images[currentIndex + 1];
  const hasPrevious = !!images[currentIndex - 1];

  const handleNext = () => {
    if (!hasNext) return;
    setCurrentIndex(currentIndex + 1);
  };

  const handlePrevious = () => {
    if (!hasPrevious) return;
    setCurrentIndex(currentIndex - 1);
  };

  const handleZoomChange = (zoom: number) => {
    setZoom(zoom);
    cropListRef.current[currentIndex].zoom = zoom;
  };

  const handleCropChange = (crop: Point) => {
    setCrop(crop);
    cropListRef.current[currentIndex].position = crop;
  };

  const handleCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      cropListRef.current[currentIndex].cropResult = croppedAreaPixels;
    },
    [currentIndex]
  );

  return (
    <CustomModal
      open={open}
      handleCloseModal={onClose}
      classes={{
        paper: classes.modalPaper,
        root: classes.rootModal,
      }}
    >
      <div className={classes.container}>
        {loading && (
          <div className={classes.loader}>
            <CircularProgress color="primary" />
            <span className={classes.loaderMessage}>
              Recortando imagen {cropImageProgress} de {images.length}
            </span>
          </div>
        )}

        <header className={classes.header}>
          <Typography className={classes.title}>Recorte de imágen</Typography>

          <Tooltip
            placement="right"
            enterTouchDelay={0}
            classes={{tooltip: classes.tooltip}}
            title={
              <Typography className={classes.textTooltip}>
                Debes recortar todas las imágenes, para poder continuar.
              </Typography>
            }
          >
            <InfoOutlined color="primary" className={classes.infoBtn} />
          </Tooltip>
        </header>
        <Typography className={classes.tip} data-error={!!error}>
          {!!error ? error : 'Asegúrate que la imágen se vea centrada.'}
        </Typography>

        {dynamicAspectRatio && (
          <div className={classes.aspectButtonContainer}>
            <CustomButton
              color={
                cropAspectRatio === dynamicAspectRatio.square.aspect
                  ? 'primary'
                  : 'default'
              }
              variant="outlined"
              fullWidth
              className={classes.aspectButton}
              onClick={() =>
                setCropAspectRatio(dynamicAspectRatio.square.aspect)
              }
            >
              <div className={classes.aspectButtonLabel}>
                <AspectSquareIcon />
                <span>{dynamicAspectRatio.rectangle.label || 'Cuadrada'}</span>
              </div>
            </CustomButton>

            <CustomButton
              color={
                cropAspectRatio === dynamicAspectRatio.rectangle.aspect
                  ? 'primary'
                  : 'default'
              }
              variant="outlined"
              fullWidth
              className={classes.aspectButton}
              onClick={() =>
                setCropAspectRatio(dynamicAspectRatio.rectangle.aspect)
              }
            >
              <div className={classes.aspectButtonLabel}>
                <AspectRectangleIcon />
                <span>{dynamicAspectRatio.square.label || 'Rectangular'}</span>
              </div>
            </CustomButton>
          </div>
        )}

        {!restrictPosition && (
          <div className={classes.customBgSection}>
            <FormControlLabel
              classes={{
                label: classes.colorInputLabel,
              }}
              control={
                <Checkbox
                  checked={enabledCustomBgColor}
                  onChange={(_, value) => setEnabledCustomBgColor(value)}
                  name="enabledCustomBgColor"
                  color="primary"
                />
              }
              label="Fondo personalizado"
            />
            <div
              className={classes.colorInput}
              data-disabled={!enabledCustomBgColor}
            >
              <ColorInput
                value={backgroundColor}
                onChange={value => setBackgroundColor(value)}
              />
            </div>
          </div>
        )}

        <div
          className={classes.cropContainer}
          style={{
            backgroundColor: enabledCustomBgColor
              ? backgroundColor
              : 'transparent',
          }}
        >
          <div className={classes.cropArea} ref={cropAreaRef}>
            {crop ? (
              <Cropper
                image={currentImage.preview}
                crop={crop}
                zoom={zoom}
                restrictPosition={restrictPosition}
                aspect={cropAspectRatio}
                onCropChange={handleCropChange}
                onCropComplete={handleCropComplete}
                onZoomChange={handleZoomChange}
              />
            ) : (
              <CircularProgress color="primary" />
            )}
            {hasPrevious && (
              <IconButton
                className={classes.navigationButton}
                data-position="left"
                disabled={!hasPrevious || !crop}
                onClick={handlePrevious}
              >
                <ArrowForwardIos
                  fontSize="small"
                  style={{transform: 'rotate(180deg)'}}
                />
              </IconButton>
            )}
            {hasNext && (
              <IconButton
                className={classes.navigationButton}
                data-position="right"
                disabled={!crop}
                onClick={handleNext}
              >
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            )}
          </div>

          <div className={classes.hintContainer}>
            <OpenWith />
            <p>Muévela para cuadrarla</p>
          </div>
        </div>
        <div className={classes.sliderContainer}>
          <ZoomSlider
            onChange={(e, value) => {
              handleZoomChange(value);
            }}
            value={zoom}
            min={ZOOM_CONFIG.min}
            step={0.1}
            max={ZOOM_CONFIG.max}
          />
        </div>

        <footer className={classes.footer}>
          <div className={classes.pagination}>
            <IconButton
              className={classes.arrowButton}
              disabled={!hasPrevious || !crop}
              onClick={handlePrevious}
            >
              <ArrowForwardIos
                fontSize="small"
                style={{transform: 'rotate(180deg)'}}
              />
            </IconButton>
            <Hidden smDown>
              <Typography>
                Imagen {currentIndex + 1} de {images.length}
              </Typography>
            </Hidden>

            <Hidden mdUp>
              <Typography>
                {currentIndex + 1} / {images.length}
              </Typography>
            </Hidden>
            <IconButton
              className={classes.arrowButton}
              disabled={!hasNext || !crop}
              onClick={handleNext}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </div>

          <CustomButton
            className={classes.submitButton}
            color="primary"
            variant="contained"
            onClick={handleConfirm}
            fullWidth
            disabled={!crop}
          >
            Confirmar
          </CustomButton>
        </footer>
      </div>
    </CustomModal>
  );
}
